import React from 'react';
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { cn } from "../../utils/cn";
import Icon from '../AppIcon';

const buttonVariants = cva(
    "inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
    {
        variants: {
            variant: {
                // Primary - Filled violet button
                default: "bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl shadow-soft hover:shadow-elevated",
                // Outlined secondary
                outline: "border-2 border-border bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground rounded-xl",
                // Secondary with lavender background
                secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-xl shadow-soft",
                // Ghost tertiary
                ghost: "bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground rounded-xl",
                // Destructive
                destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-xl shadow-soft hover:shadow-elevated",
                // Link style
                link: "text-primary underline-offset-4 hover:underline bg-transparent rounded-xl",
                // Status variants
                success: "bg-success text-success-foreground hover:bg-success/90 rounded-xl shadow-soft hover:shadow-elevated",
                warning: "bg-warning text-warning-foreground hover:bg-warning/90 rounded-xl shadow-soft hover:shadow-elevated",
                danger: "bg-error text-error-foreground hover:bg-error/90 rounded-xl shadow-soft hover:shadow-elevated",
            },
            size: {
                default: "h-10 px-6 py-2.5 text-sm",
                sm: "h-8 px-4 py-2 text-sm rounded-lg",
                lg: "h-12 px-8 py-3 text-base rounded-xl",
                icon: "h-10 w-10 rounded-xl",
                xs: "h-7 px-3 py-1.5 text-xs rounded-lg",
                xl: "h-14 px-10 py-4 text-lg rounded-2xl font-semibold",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
);

const Button = React.forwardRef(({
    className,
    variant,
    size,
    asChild = false,
    children,
    loading = false,
    iconName = null,
    iconPosition = 'left',
    iconSize = null,
    fullWidth = false,
    disabled = false,
    ...props
}, ref) => {
    const Comp = asChild ? Slot : "button";

    // Icon size mapping based on button size with modern sizing
    const iconSizeMap = {
        xs: 12,
        sm: 14,
        default: 16,
        lg: 18,
        xl: 20,
        icon: 16,
    };

    const calculatedIconSize = iconSize || iconSizeMap?.[size] || 16;

    // Modern loading spinner with smooth animation
    const LoadingSpinner = () => (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
    );

    const renderIcon = () => {
        if (!iconName) return null;
        try {
            return (
                <Icon
                    name={iconName}
                    size={calculatedIconSize}
                    className={cn(
                        children && iconPosition === 'left' && "mr-2",
                        children && iconPosition === 'right' && "ml-2"
                    )}
                />
            );
        } catch {
            return null;
        }
    };

    const renderFallbackButton = () => (
        <button
            className={cn(
                buttonVariants({ variant, size, className }),
                fullWidth && "w-full",
                "transform transition-all duration-200 ease-smooth hover:scale-[1.02] active:scale-[0.98]"
            )}
            ref={ref}
            disabled={disabled || loading}
            {...props}
        >
            {loading && <LoadingSpinner />}
            {iconName && iconPosition === 'left' && renderIcon()}
            {children}
            {iconName && iconPosition === 'right' && renderIcon()}
        </button>
    );

    // When asChild is true, merge icons into the child element
    if (asChild) {
        try {
            if (!children || React.Children?.count(children) !== 1) {
                return renderFallbackButton();
            }

            const child = React.Children?.only(children);

            if (!React.isValidElement(child)) {
                return renderFallbackButton();
            }
            const content = (
                <>
                    {loading && <LoadingSpinner />}
                    {iconName && iconPosition === 'left' && renderIcon()}
                    {child?.props?.children}
                    {iconName && iconPosition === 'right' && renderIcon()}
                </>
            );

            const clonedChild = React.cloneElement(child, {
                className: cn(
                    buttonVariants({ variant, size, className }),
                    fullWidth && "w-full",
                    "transform transition-all duration-200 ease-smooth hover:scale-[1.02] active:scale-[0.98]",
                    child?.props?.className
                ),
                disabled: disabled || loading || child?.props?.disabled,
                children: content,
            });

            return <Comp ref={ref} {...props}>{clonedChild}</Comp>;
        } catch {
            return renderFallbackButton();
        }
    }

    return (
        <Comp
            className={cn(
                buttonVariants({ variant, size, className }),
                fullWidth && "w-full",
                "transform transition-all duration-200 ease-smooth hover:scale-[1.02] active:scale-[0.98]"
            )}
            ref={ref}
            disabled={disabled || loading}
            {...props}
        >
            {loading && <LoadingSpinner />}
            {iconName && iconPosition === 'left' && renderIcon()}
            {children}
            {iconName && iconPosition === 'right' && renderIcon()}
        </Comp>
    );
});

Button.displayName = "Button";
export default Button;